import axios from "axios";
import { api } from "../config";
import { getCookie, deleteCookie } from "./cookie_helper";
import {
  isManager,
  isManagerRestrictedApiUrl,
  createAccessDeniedError,
  isReadOnlyMutationBlocked,
  createReadOnlyDeniedError,
} from "./rbac_helper";
import { notifyReadOnlyBlocked } from "./readOnlyNotify";

// default
axios.defaults.baseURL = api.API_URL;
// content type
axios.defaults.headers.post["Content-Type"] = "application/json";

// Initialize token from cookie
const initializeAuthToken = () => {
  try {
    // Try to get token from cookie first
    const token = getCookie("authToken");
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    } else {
      // Fallback to localStorage for backward compatibility
      const authUser = localStorage.getItem("authUser");
      if (authUser) {
        const parsedAuth = JSON.parse(authUser);
        const fallbackToken = parsedAuth?.token || parsedAuth?.data?.token;
        if (fallbackToken) {
          axios.defaults.headers.common["Authorization"] = `Bearer ${fallbackToken}`;
        }
      }
    }
  } catch (error) {
    console.error("Error initializing auth token:", error);
  }
};

// Initialize token on load
initializeAuthToken();

// Request interceptor to add token to each request
axios.interceptors.request.use(
  function (config) {
    try {
      const requestUrl = config.url || "";
      const requestMethod = config.method || "get";

      if (isManager() && isManagerRestrictedApiUrl(requestUrl)) {
        return Promise.reject(createAccessDeniedError());
      }

      if (isReadOnlyMutationBlocked(requestMethod, requestUrl)) {
        notifyReadOnlyBlocked();
        return Promise.reject(createReadOnlyDeniedError());
      }

      // Try to get token from cookie first
      const token = getCookie("authToken");
      if (token && !config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${token}`;
      } else {
        // Fallback to localStorage for backward compatibility
        const authUser = localStorage.getItem("authUser");
        if (authUser) {
          const parsedAuth = JSON.parse(authUser);
          const fallbackToken = parsedAuth?.token || parsedAuth?.data?.token;
          if (fallbackToken && !config.headers.Authorization) {
            config.headers.Authorization = `Bearer ${fallbackToken}`;
          }
        }
      }
    } catch (error) {
      console.error("Error setting auth token in request:", error);
    }
    return config;
  },
  function (error) {
    return Promise.reject(error);
  }
);

/**
 * Prefer detailed `error` (e.g. validation) over generic `message`.
 * Supports: { message, error: "..." } | { error: { field: "..." } } | string[]
 */
const resolveApiErrorMessage = (errorData, fallback) => {
  if (!errorData) return fallback;

  const detail = errorData.error;
  if (typeof detail === "string" && detail.trim()) {
    return detail.trim();
  }
  if (Array.isArray(detail) && detail.length) {
    return detail
      .map((item) => (typeof item === "string" ? item : item?.message || JSON.stringify(item)))
      .join(", ");
  }
  if (detail && typeof detail === "object") {
    const fieldMessages = Object.values(detail).filter(
      (v) => typeof v === "string" && v.trim()
    );
    if (fieldMessages.length) {
      return fieldMessages.join(", ");
    }
  }

  if (typeof errorData.message === "string" && errorData.message.trim()) {
    return errorData.message.trim();
  }

  return fallback;
};

// Response interceptor to handle errors
axios.interceptors.response.use(
  function (response) {
    // Return response data if available, otherwise return full response
    return response.data ? response.data : response;
  },
  function (error) {
    // Handle error response
    let message = "An error occurred";
    
    if (error.response) {
      // Server responded with error status
      const status = error.response.status;
      const errorData = error.response.data;
      
      switch (status) {
        case 400:
          message = resolveApiErrorMessage(
            errorData,
            "Bad request. Please check your input."
          );
          break;
        case 401:
          message = resolveApiErrorMessage(
            errorData,
            "Invalid credentials. Please try again."
          );
          // Clear auth data on 401
          deleteCookie("authToken");
          localStorage.removeItem("authUser");
          delete axios.defaults.headers.common["Authorization"];
          
          // Redirect to login if not already there
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
          break;
        case 403:
          message = resolveApiErrorMessage(
            errorData,
            "You don't have permission to access this resource."
          );
          break;
        case 404:
          message = resolveApiErrorMessage(
            errorData,
            "The requested resource was not found."
          );
          break;
        case 500:
          message = resolveApiErrorMessage(
            errorData,
            "Internal server error. Please try again later."
          );
          break;
        default:
          message = resolveApiErrorMessage(
            errorData,
            error.message || "An unexpected error occurred."
          );
      }
    } else if (error.request) {
      // Request was made but no response received
      message = "Network error. Please check your internet connection.";
    } else {
      // Something else happened
      message = error.message || "An unexpected error occurred.";
    }
    
    return Promise.reject({
      message,
      status: error.response?.status,
      data: error.response?.data,
    });
  }
);
/**
 * Sets the default authorization token
 * @param {string} token - Authentication token
 */
const setAuthorization = (token) => {
  if (token) {
    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  } else {
    delete axios.defaults.headers.common["Authorization"];
  }
};

/**
 * Clears authorization token
 */
const clearAuthorization = () => {
  delete axios.defaults.headers.common["Authorization"];
};

class APIClient {
  /**
   * Fetches data from given url
   */

  //  get = (url, params) => {
  //   return axios.get(url, params);
  // };
  get = (url, params) => {
    let response;

    let paramKeys = [];

    if (params) {
      Object.keys(params).map(key => {
        paramKeys.push(key + '=' + params[key]);
        return paramKeys;
      });

      const queryString = paramKeys && paramKeys.length ? paramKeys.join('&') : "";
      response = axios.get(`${url}?${queryString}`, params);
    } else {
      response = axios.get(`${url}`, params);
    }

    return response;
  };
  /**
   * post given data to url
   * @param {string} url
   * @param {object} data
   * @param {object} [config] optional axios config (e.g. MFA session Authorization header)
   */
  create = (url, data, config = {}) => {
    return axios.post(url, data, config);
  };
  /**
   * Updates data
   */
  update = (url, data) => {
    return axios.patch(url, data);
  };

  put = (url, data) => {
    return axios.put(url, data);
  };
  /**
   * Delete
   */
  delete = (url, config) => {
    return axios.delete(url, { ...config });
  };
}
const getLoggedinUser = () => {
  // Check if token exists in cookie
  const token = getCookie("authToken");
  
  // Get user data from localStorage (token is in cookie, user data in localStorage)
  const user = localStorage.getItem("authUser");
  
  // If no token in cookie and no user data, return null
  if (!token && !user) {
    return null;
  }
  
  // If we have user data, parse and return it
  if (user) {
    try {
      const parsedUser = JSON.parse(user);
      // Ensure token is set from cookie if available
      if (token && !parsedUser.token) {
        parsedUser.token = token;
      }
      return parsedUser;
    } catch (error) {
      console.error("Error parsing user data:", error);
      return null;
    }
  }
  
  // If only token exists but no user data, return minimal object
  if (token) {
    return { token };
  }
  
  return null;
};

export { APIClient, setAuthorization, clearAuthorization, getLoggedinUser };